<template>
  <CardBase clicavel @click="$emit('instalar', conector)">
    <template #logo>
      <LogoIntegracao
        :logo="conector.logo"
        :nome="conector.nome"
        :icone="conector.icone"
        tamanho="grande"
      />
    </template>

    <template #selos>
      <!--
        Parceria vem antes de tudo: é ela que diz que a integração foi feita
        junto com o fabricante, e não por engenharia reversa da API pública.
      -->
      <span
        v-if="conector.parceria_rotulo"
        class="selo selo--parceiro"
        :title="'Integração homologada junto com ' + conector.nome"
      >
        <fa-icon icon="award" /> {{ conector.parceria_rotulo }}
      </span>
      <span v-if="conector.situacao === 'BETA'" class="selo selo--beta">Beta</span>
      <span v-if="instalado" class="selo selo--instalado">
        <fa-icon icon="check" /> Instalada
      </span>
      <!--
        Sem a licença o conector continua à vista: esconder faria o cliente não
        saber que o recurso existe, e é justamente daqui que ele descobre o que
        pode contratar.
      -->
      <span
        v-if="!licenciado"
        class="selo selo--licenca"
        :title="'Depende do módulo ' + conector.licenca_nome + ', não licenciado nesta instalação.'"
      >
        <fa-icon icon="lock" /> {{ conector.licenca_nome }}
      </span>
    </template>

    <template #nome>{{ conector.nome }}</template>
    <template #subtitulo>{{ conector.categoria_nome }}</template>

    <p class="card-conector__descricao">{{ conector.descricao_curta }}</p>

    <!--
      O que a ferramenta entrega. Com um conector por gateway, é o selo que
      diferencia quem só cota de quem também rastreia.
    -->
    <div v-if="capacidades.length" class="card-conector__capacidades">
      <span v-for="rotulo in capacidades" :key="rotulo" class="capacidade">
        {{ rotulo }}
      </span>
    </div>

    <template #rodape-livre>
      <!--
        As marcas dos provedores só aparecem quando acrescentam informação. Num
        conector de um provedor só — a própria ferramenta —, repetir a mesma
        arte logo abaixo do topo é ruído.
      -->
      <div v-if="marcasVisiveis.length" class="card-conector__marcas">
        <LogoIntegracao
          v-for="marca in marcasVisiveis"
          :key="marca.chave"
          :logo="marca.logo"
          :nome="marca.nome"
          :cor="marca.cor"
          :title="marca.nome"
          usar-monograma
          tamanho="pequeno"
        />
          <span v-if="marcasOcultas" class="card-conector__marcas-extra">
          +{{ marcasOcultas }}
        </span>
      </div>

      <!--
        Quem responde pela integração. O nome do produto nem sempre é o da
        empresa — o Roteiriza.ai é da Soha —, e é com a empresa que existe o
        acordo de homologação.
      -->
      <div v-if="conector.parceiro_nome" class="card-conector__parceiro">
        <small>por</small>
        <!-- Com a marca à vista, repetir o nome ao lado dela só ocupa espaço. -->
        <img
          v-if="conector.parceiro_logo"
          class="card-conector__parceiro-logo"
          :src="conector.parceiro_logo"
          :alt="conector.parceiro_nome"
          :title="conector.parceiro_nome"
        />
        <span v-else class="card-conector__parceiro-nome">{{ conector.parceiro_nome }}</span>
      </div>
    </template>

    <template #rodape>
      <a
        v-if="siteDoParceiro"
        class="card-conector__site"
        :href="siteDoParceiro"
        target="_blank"
        rel="noopener"
        @click.stop
      >
        <fa-icon icon="globe" /> Site do parceiro
      </a>
      <span v-else class="card-conector__provedores">{{ resumoProvedores }}</span>
      <span
        class="card-conector__acao"
        :class="{ 'card-conector__acao--bloqueada': !licenciado }"
      >
        {{ licenciado ? rotuloAcao : 'Conhecer' }} <fa-icon icon="angle-right" />
      </span>
    </template>
  </CardBase>
</template>

<script>
import { urlSegura } from '@/helpers/urlSegura';

import CardBase from './CardBase';
import LogoIntegracao from './LogoIntegracao';

export default {
  name: 'CardConector',
  components: { CardBase, LogoIntegracao },
  props: {
    conector: {
      type: Object,
      required: true,
    },
  },
  computed: {
    /* O endereço do parceiro vem do manifesto; só vira link se for http(s). */
    siteDoParceiro() {
      return urlSegura(this.conector && this.conector.site);
    },

    /*
     * O cartão abre de qualquer jeito: é na tela do conector que o cliente vê o
     * que o recurso faz e como contratá-lo. Aqui a licença só muda o que o
     * rodapé promete — "Conhecer" em vez de "Instalar".
     */
    licenciado() {
      return this.conector.licenca_liberada !== false;
    },

    instalado() {
      return this.conector.instalacoes > 0;
    },

    /*
     * Conector de instância única já instalado não oferece "Instalar" — o clique
     * leva para a configuração existente. Multi-instância sempre pode receber
     * mais uma (outro SMTP, outra filial).
     */
    rotuloAcao() {
      if (this.instalado && !this.conector.multi_instancia) {
        return 'Configurar';
      }

      return this.instalado ? 'Adicionar outra' : 'Instalar';
    },

    /*
     * As marcas dos provedores, e não só o nome do conector: é o logotipo da
     * OpenAI ou da SimFrete que identifica a integração de relance, não o
     * ícone temático que o conector compartilha com os vizinhos da categoria.
     *
     * Só entra quem tem marca declarada. Vários "provedores" são modos de
     * operação, não empresas — "Padrão", "Somente leitura", "Frete próprio" —
     * e transformá-los em selo encheria o card de iniciais sem significado.
     */
    marcasDistintas() {
      const vistas = new Set();

      return this.provedores
        /*
         * Com o conector já identificado pelo próprio logotipo, só acrescenta
         * informação a marca que também tem arte — as iniciais coloridas de um
         * provedor ("OP", "RV") viravam ruído logo abaixo dele.
         */
        .filter((provedor) => provedor.marca && (provedor.marca.logo || !this.conector.logo))
        .map((provedor) => ({
          chave: provedor.chave,
          nome: provedor.nome,
          logo: provedor.marca.logo || null,
          cor: provedor.marca.cor || null,
        }))
        .filter((marca) => {
          // Versões do mesmo produto (reCAPTCHA v2, v3 e Enterprise) trazem a
          // mesma identidade; repetir o selo do Google três vezes não informa
          // nada além de ocupar espaço.
          const identidade = marca.logo || marca.cor;

          if (!identidade || vistas.has(identidade)) {
            return false;
          }

          vistas.add(identidade);

          return true;
        });
    },

    marcas() {
      return this.marcasDistintas.slice(0, 4);
    },

    /*
     * Some com a fileira de marcas quando ela não diz nada de novo: conector de
     * um provedor só, cuja arte já é a do topo do card. Com dois ou mais, ela
     * volta — é o que mostra, de relance, com quais serviços aquele conector
     * fala.
     */
    marcasVisiveis() {
      if (this.marcas.length > 1) {
        return this.marcas;
      }

      const unica = this.marcas[0];

      if (!unica) {
        return [];
      }

      return unica.logo && unica.logo === this.conector.logo ? [] : this.marcas;
    },

    marcasOcultas() {
      return Math.max(this.marcasDistintas.length - 4, 0);
    },

    provedores() {
      return (this.conector.manifesto && this.conector.manifesto.provedores) || [];
    },

    capacidades() {
      return this.conector.capacidades_rotulos || [];
    },

    resumoProvedores() {
      const provedores = this.provedores;

      if (provedores.length <= 1) {
        return this.conector.multi_instancia ? 'Permite várias configurações' : '';
      }

      return `${provedores.length} provedores disponíveis`;
    },
  },
};
</script>

<style scoped>
.card-conector__marcas {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 20px 14px;
}

.card-conector__marcas-extra {
  font-size: 11px;
  font-weight: 600;
  color: #98a2b3;
}

.selo {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.6;
}

.selo--licenca {
  background: #fef8e7;
  color: #8a6d1f;
}

.selo--instalado {
  background: #e7f6ec;
  color: #1c7a44;
}

.selo--beta {
  background: #fef4e6;
  color: #b25e09;
}

.selo--parceiro {
  background: #edf8f9;
  color: #14727c;
  border: 1px solid #c5e6e9;
}

.card-conector__parceiro {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 20px 12px;
}

.card-conector__parceiro-logo {
  max-height: 18px;
  max-width: 90px;
  object-fit: contain;
}

.card-conector__parceiro-nome {
  font-weight: 700;
  font-size: 12px;
  color: #3a3e81;
}

.card-conector__parceiro small {
  font-size: 11px;
  color: #98a2b3;
}

.card-conector__site {
  font-size: 12px;
  color: #2298a3;
  text-decoration: none;
}

.card-conector__site:hover {
  text-decoration: underline;
}

.card-conector__descricao {
  margin: 10px 0 0;
  font-size: 13px;
  color: #667085;
  line-height: 1.5;
}

.card-conector__capacidades {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
}

.capacidade {
  padding: 2px 8px;
  border-radius: 10px;
  background: #f2f4f7;
  color: #475467;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.card-conector__provedores {
  color: #667085;
}

.card-conector__acao--bloqueada {
  color: #98a2b3;
  font-weight: 500;
}

.card-conector__acao {
  color: #3a3e81;
  font-weight: 600;
  white-space: nowrap;
  margin-left: auto;
}

</style>
